import { useEffect, useState } from "react";
import { useUnitDisplay } from "../../app/units/UnitDisplayContext";
import { reportLifecycleDiagnostic } from "../../app/api/lifecycleDiagnostics";

import { catalogApi } from "../catalog/api";
import { networkImportApi } from "./api";

import type { CatalogApi } from "../catalog/api";
import type { HoseTypeRead } from "../catalog/types";
import type { NetworkImportApi } from "./api";
import type {
  FeatureRoleMapping,
  ImportedFeature,
  ImportedGeometry,
  ImportedPosition,
  ImportPreview,
  ImportRole,
  ProjectDocument,
} from "./types";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { networkFieldHelp } from "../../app/guidance/fieldHelpRegistry";

const ROLE_OPTIONS: Array<{ label: string; value: ImportRole }> = [
  { label: "Source", value: "source" },
  { label: "Sink", value: "sink" },
  { label: "Junction", value: "junction" },
  { label: "Hose link", value: "hose_link" },
  { label: "Approved pump site", value: "approved_pump_site" },
  { label: "Permitted route / corridor", value: "permitted_route" },
  { label: "Permit area", value: "permit_area" },
  { label: "Exclusion area", value: "exclusion_area" },
  { label: "Reference", value: "reference" },
  { label: "Ignore", value: "ignore" },
];

function mappingForFeature(feature: ImportedFeature): FeatureRoleMapping {
  return {
    accept_imported_altitude_as_manual: false,
    allowed_arrangements: ["series", "parallel"],
    copy_to_hose_alignment: false,
    feature_id: feature.id,
    hose_type_id: null,
    role: feature.suggested_role,
    site_capacity: 1,
  };
}

function positions(geometry: ImportedGeometry | null): ImportedPosition[] {
  if (geometry === null) {
    return [];
  }
  switch (geometry.type) {
    case "Point":
      return [geometry.coordinates];
    case "LineString":
      return geometry.coordinates;
    case "Polygon":
      return geometry.coordinates.flat();
  }
}

function geometrySummary(geometry: ImportedGeometry | null): string {
  if (geometry === null) {
    return "Unsupported or malformed geometry";
  }
  const coordinateText = positions(geometry)
    .slice(0, 3)
    .map(
      (point) =>
        `${point.longitude_deg.toString()}, ${point.latitude_deg.toString()}`,
    )
    .join(" · ");
  return `${geometry.type}: ${coordinateText} · ${positions(geometry).length} coordinates`;
}

function hasImportedAltitude(feature: ImportedFeature): boolean {
  return positions(feature.geometry).some(
    (position) => position.imported_altitude_m !== null,
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The local import failed.";
}

export interface ImportRoleMapperProps {
  api?: NetworkImportApi;
  catalog?: Pick<CatalogApi, "listHoses">;
  onProjectSaved?: (project: ProjectDocument) => Promise<void> | void;
  project: ProjectDocument;
}

export function ImportRoleMapper({
  api = networkImportApi,
  catalog = catalogApi,
  onProjectSaved,
  project,
}: ImportRoleMapperProps) {
  const { formatSiValue } = useUnitDisplay();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mappings, setMappings] = useState<Record<string, FeatureRoleMapping>>(
    {},
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoses, setHoses] = useState<HoseTypeRead[]>([]);
  const [hoseCatalogError, setHoseCatalogError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    void catalog.listHoses("").then(
      (page) => {
        if (!current) return;
        setHoses(page.items);
        setHoseCatalogError(null);
      },
      (caught: unknown) => {
        if (current) setHoseCatalogError(errorMessage(caught));
      },
    );
    return () => {
      current = false;
    };
  }, [catalog]);

  const previewFile = async (selectedFile: File) => {
    reportLifecycleDiagnostic("import_preview_started", "network", "started");
    setBusy(true);
    setError(null);
    try {
      const nextPreview = await api.previewImport(selectedFile);
      setFile(selectedFile);
      setPreview(nextPreview);
      setMappings(
        Object.fromEntries(
          nextPreview.features.map((feature) => [
            feature.id,
            mappingForFeature(feature),
          ]),
        ),
      );
      reportLifecycleDiagnostic(
        "import_preview_completed",
        "network",
        "succeeded",
      );
    } catch (caught) {
      setFile(null);
      setPreview(null);
      setMappings({});
      setError(errorMessage(caught));
      reportLifecycleDiagnostic("import_preview_failed", "network", "failed");
    } finally {
      setBusy(false);
    }
  };

  const updateMapping = (
    featureId: string,
    update: Partial<FeatureRoleMapping>,
  ) => {
    setMappings((current) => {
      const mapping = current[featureId];
      if (mapping === undefined) {
        return current;
      }
      return { ...current, [featureId]: { ...mapping, ...update } };
    });
  };

  const saveImport = async () => {
    if (file === null || preview === null) {
      return;
    }
    const orderedMappings = preview.features
      .map((feature) => mappings[feature.id])
      .filter(
        (mapping): mapping is FeatureRoleMapping => mapping !== undefined,
      );
    if (orderedMappings.length !== preview.features.length) {
      setError("Choose a role for every preview feature before saving.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await api.applyImport(
        project.id,
        project.revision,
        file,
        orderedMappings,
      );
      await onProjectSaved?.(saved);
      setFile(null);
      setPreview(null);
      setMappings({});
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const complete =
    preview !== null &&
    preview.features.every((feature) => {
      const mapping = mappings[feature.id];
      if (mapping === undefined) return false;
      const createsHose =
        mapping.role === "hose_link" || mapping.copy_to_hose_alignment;
      return !createsHose || mapping.hose_type_id !== null;
    });

  return (
    <section aria-labelledby="local-import-title" className="network-panel">
      <h2 id="local-import-title">Import a route</h2>
      <p>KML, KMZ, or GeoJSON.</p>
      <label
        className="network-drop-zone"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const dropped = event.dataTransfer.files.item(0);
          if (dropped !== null) {
            reportLifecycleDiagnostic(
              "file_selection_completed",
              "network",
              "succeeded",
            );
            void previewFile(dropped);
          }
        }}
      >
        Choose or drop a file
        <input
          {...guided(networkFieldHelp.importFile)}
          accept=".geojson,.json,.kml,.kmz,application/geo+json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
          aria-label="Geospatial import file"
          disabled={busy}
          onChange={(event) => {
            const selected = event.currentTarget.files?.[0];
            if (selected !== null && selected !== undefined) {
              reportLifecycleDiagnostic(
                "file_selection_completed",
                "network",
                "succeeded",
              );
              void previewFile(selected);
            } else {
              reportLifecycleDiagnostic(
                "file_selection_cancelled",
                "network",
                "cancelled",
              );
            }
          }}
          onClick={() =>
            reportLifecycleDiagnostic(
              "file_selection_opened",
              "network",
              "started",
            )
          }
          type="file"
        />
      </label>
      {busy ? <p role="status">Working with the local file…</p> : null}
      {error === null ? null : <p role="alert">{error}</p>}
      {hoseCatalogError === null ? null : (
        <p role="alert">Active hose catalog unavailable: {hoseCatalogError}</p>
      )}

      {preview === null ? null : (
        <div className="network-import-preview">
          <h3>
            {preview.original_filename} · {preview.features.length} features
          </h3>
          <details className="network-technical">
            <summary>Import details</summary>
            <p>
              {preview.source_format.toUpperCase()} · parser{" "}
              {preview.parser_version} · SHA-256 {preview.source_sha256}
            </p>
          </details>
          {preview.issues.length === 0 ? null : (
            <ul aria-label="Import issues">
              {preview.issues.map((issue, index) => (
                <li
                  key={`${issue.code}-${issue.feature_id ?? "document"}-${index}`}
                >
                  {issue.severity}: {issue.message}
                </li>
              ))}
            </ul>
          )}
          <ol className="network-feature-list">
            {preview.features.map((feature) => {
              const mapping = mappings[feature.id];
              if (mapping === undefined) {
                return null;
              }
              return (
                <li key={feature.id}>
                  <h4>{feature.name}</h4>
                  <details className="network-technical">
                    <summary>Technical details</summary>
                    <dl>
                      <dt>Folder</dt>
                      <dd>
                        {feature.container_path.join(" / ") || "Document root"}
                      </dd>
                      <dt>Source ID</dt>
                      <dd>{feature.source_feature_id}</dd>
                      <dt>Geometry</dt>
                      <dd>{geometrySummary(feature.geometry)}</dd>
                      <dt>Coordinate count</dt>
                      <dd>{positions(feature.geometry).length}</dd>
                      <dt>Style reference (retained inertly)</dt>
                      <dd>{feature.style.style_url ?? "None"}</dd>
                      <dt>External metadata references (never fetched)</dt>
                      <dd>
                        {JSON.stringify([
                          ...feature.style.external_references,
                          ...(feature.style.icon_href === null
                            ? []
                            : [feature.style.icon_href]),
                        ])}
                      </dd>
                      <dt>Description</dt>
                      <dd>{feature.description_text ?? "None"}</dd>
                      <dt>Extended data</dt>
                      <dd>{JSON.stringify(feature.extended_data)}</dd>
                    </dl>
                  </details>
                  <label>
                    Role for {feature.name}
                    <select
                      {...guided(networkFieldHelp.importRole, feature.id)}
                      aria-label={`Role for ${feature.name}`}
                      onChange={(event) => {
                        const role = event.currentTarget.value as ImportRole;
                        updateMapping(feature.id, {
                          copy_to_hose_alignment:
                            role === "permitted_route"
                              ? mapping.copy_to_hose_alignment
                              : false,
                          role,
                        });
                      }}
                      value={mapping.role}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {mapping.role === "permitted_route" ? (
                    <label>
                      <input
                        {...guided(
                          networkFieldHelp.copyPermittedLine,
                          feature.id,
                        )}
                        checked={mapping.copy_to_hose_alignment}
                        onChange={(event) =>
                          updateMapping(feature.id, {
                            copy_to_hose_alignment: event.currentTarget.checked,
                          })
                        }
                        type="checkbox"
                      />
                      Use as planned hose route
                    </label>
                  ) : null}
                  {mapping.role === "hose_link" ||
                  mapping.copy_to_hose_alignment ? (
                    <label>
                      Hose type for {feature.name}
                      <select
                        {...guided(
                          networkFieldHelp.importedHoseType,
                          feature.id,
                        )}
                        aria-label={`Hose type for ${feature.name}`}
                        onChange={(event) =>
                          updateMapping(feature.id, {
                            hose_type_id: event.currentTarget.value || null,
                          })
                        }
                        value={mapping.hose_type_id ?? ""}
                      >
                        <option value="">Select an active catalog hose</option>
                        {hoses.map((hose) => (
                          <option key={hose.id} value={hose.id}>
                            {hose.name} ·{" "}
                            {hose.manufacturer ?? "Unspecified manufacturer"}{" "}
                            {hose.model} ·{" "}
                            {formatSiValue(hose.inside_diameter_m, "diameter")}{" "}
                            ID
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {hasImportedAltitude(feature) ? (
                    <label>
                      <input
                        {...guided(networkFieldHelp.acceptAltitude, feature.id)}
                        checked={mapping.accept_imported_altitude_as_manual}
                        onChange={(event) =>
                          updateMapping(feature.id, {
                            accept_imported_altitude_as_manual:
                              event.currentTarget.checked,
                          })
                        }
                        type="checkbox"
                      />
                      Explicitly accept imported altitude as manual elevation
                    </label>
                  ) : null}
                </li>
              );
            })}
          </ol>
          <button
            disabled={busy || !complete || !preview.can_persist}
            onClick={() => void saveImport()}
            type="button"
          >
            Save import
          </button>
        </div>
      )}

      {project.imports.length === 0 ? null : (
        <div className="network-saved-imports">
          <h3>Saved imports</h3>
          {project.imports.map((record) => (
            <details
              key={`${record.preview.source_sha256}-${record.preview.imported_at}`}
            >
              <summary>
                {record.preview.original_filename} —{" "}
                {record.preview.features.length} features
              </summary>
              <ul>
                {record.preview.features.map((feature) => {
                  const mapping = record.mappings.find(
                    (candidate) => candidate.feature_id === feature.id,
                  );
                  return (
                    <li key={feature.id}>
                      {feature.container_path.join(" / ") || "Document root"} /{" "}
                      {feature.name} — {geometrySummary(feature.geometry)} —
                      role {mapping?.role ?? "missing"}
                      {mapping?.copy_to_hose_alignment === true
                        ? " (copied to hose alignment)"
                        : ""}
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
